import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { formatDateLabel } from "./dateUtils";
import { parseToDate } from "../../utils/dateUtils";
import {
  DownloadOption,
  FeedbackState,
  OutingGroup,
  OutingStatusRow,
  UseOutingsOptions,
} from "./types";

type OutingApiRow = Partial<OutingStatusRow> &
  Record<string, string | number | boolean | null | undefined | string[] | number[]>;

const asText = (value: OutingApiRow[string]): string | undefined =>
  typeof value === "string" || typeof value === "number" ? String(value) : undefined;

const asBool = (value: OutingApiRow[string]): boolean | null | undefined =>
  typeof value === "boolean" ? value : null;

/** Values parents typed end up in the printed HTML: escape them so they stay text. */
const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toMillis = (val: string | number | Date | undefined | null): number => {
  if (!val) return Number.POSITIVE_INFINITY;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? Number.POSITIVE_INFINITY : d.getTime();
};

const inclusiveExpiresParam = (value?: string | null) => {
  if (!value) return null;
  const num = Number(value);
  if (Number.isFinite(num)) {
    // interpret seconds vs millis, then add 24h to include the full expiration day
    const isMillis = num > 1e12;
    const adjusted = num + (isMillis ? 86_400_000 : 86_400);
    return String(adjusted);
  }
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) {
    return new Date(dt.getTime() + 86_400_000).toISOString();
  }
  return value;
};

const groupOutings = (rows: OutingApiRow[]): OutingGroup[] => {
  const grouped = new Map<string, OutingGroup>();

  rows.forEach((row) => {
    const outingId = asText(row.outingId) || asText(row.requestId) || "";
    const baseName = asText(row.name) || asText(row.tripName) || asText(row.requestName) || asText(row.outingName) || asText(row.title) || asText(row.label) || "";
    const startsAt =
      asText(row.startsAt) ||
      asText(row.startAt) ||
      asText(row.starts_at) ||
      asText(row.requestStartsAt) ||
      asText(row.startDate) ||
      asText(row.startsOn) ||
      asText(row.from) ||
      "";
    const expiresAt =
      asText(row.expiresAt) ||
      asText(row.expires_at) ||
      asText(row.requestExpiresAt) ||
      asText(row.endDate) ||
      asText(row.endsOn) ||
      asText(row.to) ||
      "";

    const key = `${outingId}|${baseName}|${startsAt}|${expiresAt}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        outingId,
        name: baseName,
        startsAt,
        expiresAt,
        children: [],
        requiresDualConsent: !!row.requiresDualConsent,
        consentsRequired:
          typeof row.consentsRequired === "number"
            ? row.consentsRequired
            : row.requiresDualConsent
              ? 2
              : 1,
        consentsGiven: typeof row.consentsGiven === "number" ? row.consentsGiven : 0,
        parent1ConsentedAt: asText(row.parent1ConsentedAt) || null,
        parent2ConsentedAt: asText(row.parent2ConsentedAt) || null,
        total: 0,
        filled: 0,
        consentYes: 0,
        consentNo: 0,
      });
    }

    const bucket = grouped.get(key)!;

    bucket.children.push({
      childId: asText(row.childId),
      childName: asText(row.childName),
      submittedAt: asText(row.submittedAt),
      tripConsent: asBool(row.tripConsent) ?? undefined,
      status: asText(row.childStatus) || asText(row.status) || asText(row.outingStatus) || asText(row.state) || null,
      requestId: asText(row.requestId) || asText(row.outingRequestId) || asText(row.id) || null,
    });

    if (asText(row.parent1ConsentedAt)) bucket.parent1ConsentedAt = asText(row.parent1ConsentedAt) ?? null;
    if (asText(row.parent2ConsentedAt)) bucket.parent2ConsentedAt = asText(row.parent2ConsentedAt) ?? null;
    if (typeof row.consentsGiven === "number") bucket.consentsGiven = row.consentsGiven;
    if (typeof row.consentsRequired === "number") bucket.consentsRequired = row.consentsRequired;
  });

  return Array.from(grouped.values()).map((item) => {
    const total = item.children.length;
    const filled = item.children.filter((c) => c.submittedAt).length;
    const consentYes = item.children.filter((c) => c.tripConsent === true).length;
    const consentNo = item.children.filter((c) => c.tripConsent === false).length;

    return {
      ...item,
      total,
      filled,
      consentYes,
      consentNo,
      pendingConsent:
        item.requiresDualConsent &&
        (item.consentsGiven ?? 0) < (item.consentsRequired ?? (item.requiresDualConsent ? 2 : 1)),
    };
  });
};

const recalcOutingStats = (item: OutingGroup): OutingGroup => {
  const total = item.children.length;
  const filled = item.children.filter((c) => c.submittedAt).length;
  const consentYes = item.children.filter((c) => c.tripConsent === true).length;
  const consentNo = item.children.filter((c) => c.tripConsent === false).length;

  return { ...item, total, filled, consentYes, consentNo };
};

export const useOutings = ({ classroomId, canRead, canManage, t }: UseOutingsOptions) => {
  const [tripActionFeedback, setTripActionFeedback] = useState<FeedbackState | null>(null);
  const [tripRequestLoading, setTripRequestLoading] = useState(false);
  const [tripExportLoading, setTripExportLoading] = useState(false);
  const [outingActionLoading, setOutingActionLoading] = useState(false);

  const [showOutingModal, setShowOutingModal] = useState(false);
  const [outingStartsAt, setOutingStartsAt] = useState("");
  const [outingExpiresAt, setOutingExpiresAt] = useState("");
  const [outingName, setOutingName] = useState("");
  const [outingAddToClassroomCalendar, setOutingAddToClassroomCalendar] = useState(false);
  const [outingModalError, setOutingModalError] = useState<string | null>(null);
  const [showOutingOverviewModal, setShowOutingOverviewModal] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadOptions, setDownloadOptions] = useState<DownloadOption[]>([]);
  const [downloadOptionsLoading, setDownloadOptionsLoading] = useState(false);
  const [selectedDownloadOption, setSelectedDownloadOption] = useState<DownloadOption | null>(null);

  const [outingStatus, setOutingStatus] = useState<OutingStatusRow[]>([]);
  const [outingStatusLoading, setOutingStatusLoading] = useState(false);

  const [outingOverview, setOutingOverview] = useState<OutingGroup[]>([]);
  const [outingOverviewLoading, setOutingOverviewLoading] = useState(false);
  const [outingStatusExpanded, setOutingStatusExpanded] = useState<Record<string, boolean>>({});
  const [activeOutingKey, setActiveOutingKey] = useState<string | null>(null);

  const [showStatusPopover, setShowStatusPopover] = useState(false);
  const [statusPopoverOuting, setStatusPopoverOuting] = useState<OutingGroup | null>(null);
  const [showOutingMenu, setShowOutingMenu] = useState(false);

  const outingMenuRef = useRef<HTMLDivElement | null>(null);
  const outingMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const statusPopoverRef = useRef<HTMLDivElement | null>(null);

  const sortedOutings = useMemo(
    () => [...outingOverview].sort((a, b) => toMillis(a.startsAt || a.expiresAt) - toMillis(b.startsAt || b.expiresAt)),
    [outingOverview]
  );

  const firstOuting = sortedOutings[0] || null;

  const fetchOutingOverview = useCallback(async () => {
    if (!canRead || !classroomId) return;

    try {
      setOutingOverviewLoading(true);
      const { data } = await http.get(`/outing-info/classrooms/${classroomId}`);
      const rows = Array.isArray(data) ? data : [];
      const grouped = groupOutings(rows);
      setOutingOverview(grouped);

      if (grouped.length && !activeOutingKey) {
        setActiveOutingKey(grouped[0].key);
      }
    } catch (err) {
      setOutingOverview([]);
    } finally {
      setOutingOverviewLoading(false);
    }
  }, [activeOutingKey, canRead, classroomId]);

  const submitOutingRequest = useCallback(async () => {
    if (tripRequestLoading || !canManage) return;

    setTripActionFeedback(null);
    setOutingModalError(null);

    try {
      setTripRequestLoading(true);

      const expiresAt = outingExpiresAt?.trim() ? outingExpiresAt.trim() : null;
      const startsAt = outingStartsAt?.trim() ? outingStartsAt.trim() : null;
      const name = outingName?.trim() ? outingName.trim() : null;

      await http.post("/outing-info/requests", {
        classroomId,
        startsAt,
        expiresAt,
        name,
        addToClassroomCalendar: outingAddToClassroomCalendar,
      });

      setTripActionFeedback({
        type: "success",
        message: t("classrooms.details.trip_request_started", {
          defaultValue: "Ouders ontvangen het toestemmingsformulier in het ouderportaal.",
        }),
      });

      setShowOutingModal(false);
      setOutingStartsAt("");
      setOutingExpiresAt("");
      setOutingName("");
      setOutingAddToClassroomCalendar(false);
      await fetchOutingOverview();
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      const message =
        typeof mapped === "string"
          ? mapped
          : mapped?.message ||
            t("classrooms.details.trip_request_failed", {
              defaultValue: "Aanvragen van formulieren is mislukt.",
            });

      setOutingModalError(message);
      setTripActionFeedback({
        type: "error",
        message,
      });
    } finally {
      setTripRequestLoading(false);
    }
  }, [
    canManage,
    classroomId,
    fetchOutingOverview,
    outingAddToClassroomCalendar,
    outingExpiresAt,
    outingName,
    outingStartsAt,
    t,
    tripRequestLoading,
  ]);

  const fetchOutingStatus = useCallback(
    async (option: DownloadOption | null) => {
      if (!option) return;

      try {
        setOutingStatusLoading(true);

        const params: Record<string, string> = {};
        if (option.startsAt) params.startsAt = option.startsAt;

        const inclusiveExpire = inclusiveExpiresParam(option.expiresAt);
        if (inclusiveExpire) params.expiresAt = inclusiveExpire;
        if (option.name) params.name = option.name;
        if (option.outingId) params.outingId = option.outingId;

        const { data } = await http.get(`/outing-info/classrooms/${classroomId}`, { params });
        setOutingStatus(Array.isArray(data) ? data : []);
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setTripActionFeedback({
          type: "error",
          message:
            typeof mapped === "string"
              ? mapped
              : mapped?.message ||
                t("classrooms.details.trip_export_failed", {
                  defaultValue: "Ophalen van status is mislukt.",
                }),
        });
        setOutingStatus([]);
      } finally {
        setOutingStatusLoading(false);
      }
    },
    [classroomId, t]
  );

  const loadDownloadOptions = useCallback(
    async (classroom?: string, preferred?: DownloadOption | null) => {
      const target = classroom || classroomId;
      if (!target) return;
      try {
        setDownloadOptionsLoading(true);

        const { data } = await http.get(`/outing-info/classrooms/${target}`);
        const list = Array.isArray(data) ? data : [];
        const grouped = new Map<string, DownloadOption>();

        list.forEach((row) => {
          const baseName = row.name || row.tripName || row.requestName || row.outingName || row.title || row.label || "";
          const outingId = row.outingId || row.requestId || "";
          const startsAt =
            row.startsAt ||
            row.startAt ||
            row.starts_at ||
            row.requestStartsAt ||
            row.startDate ||
            row.startsOn ||
            row.from ||
            "";
          const expiresAt =
            row.expiresAt ||
            row.expires_at ||
            row.requestExpiresAt ||
            row.endDate ||
            row.endsOn ||
            row.to ||
            "";

          const key = `${outingId}|${baseName}|${startsAt}|${expiresAt}`;

          if (!grouped.has(key)) {
            grouped.set(key, {
              key,
              name: baseName,
              outingId,
              startsAt,
              expiresAt,
            });
          }
        });

        const options = Array.from(grouped.values());
        setDownloadOptions(options);

        const preferredOption = preferred?.outingId
          ? options.find((option) => option.outingId === preferred.outingId)
          : undefined;
        if (preferredOption) {
          setSelectedDownloadOption(preferredOption);
          fetchOutingStatus(preferredOption);
        } else if (options.length === 1) {
          setSelectedDownloadOption(options[0]);
          fetchOutingStatus(options[0]);
        }
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setTripActionFeedback({
          type: "error",
          message:
            typeof mapped === "string"
              ? mapped
              : mapped?.message ||
                t("classrooms.details.trip_export_failed", {
                  defaultValue: "Download van formulieren is mislukt.",
                }),
        });
      } finally {
        setDownloadOptionsLoading(false);
      }
    },
    [classroomId, fetchOutingStatus, t]
  );

  const handleDownloadTripForms = useCallback(async () => {
    if (tripExportLoading) return;

    setTripActionFeedback(null);

    if (!selectedDownloadOption) {
      setTripActionFeedback({
        type: "error",
        message: t("classrooms.details.trip_export_select", {
          defaultValue: "Kies eerst een uitje om te downloaden.",
        }),
      });
      return;
    }

    try {
      setTripExportLoading(true);

      const params: Record<string, string> = {};
      if (selectedDownloadOption.startsAt) params.startsAt = selectedDownloadOption.startsAt;

      const inclusiveExpire = inclusiveExpiresParam(selectedDownloadOption.expiresAt);
      if (inclusiveExpire) params.expiresAt = inclusiveExpire;
      if (selectedDownloadOption.name) params.name = selectedDownloadOption.name;
      if (selectedDownloadOption.outingId) params.outingId = selectedDownloadOption.outingId;

      const { data } = await http.get(`/outing-info/classrooms/${classroomId}`, { params });
      const list: OutingApiRow[] = Array.isArray(data) ? (data as OutingApiRow[]) : [];

      if (!list.length) {
        setTripActionFeedback({
          type: "error",
          message: t("classrooms.details.trip_export_empty", {
            defaultValue: "No completed forms found.",
          }),
        });
        setShowDownloadModal(false);
        return;
      }

      const formatBool = (val: boolean | null | undefined) => (val === true ? "Ja" : val === false ? "Nee" : "—");
      const title =
        selectedDownloadOption.name ||
        t("classrooms.details.trip_download_unnamed", { defaultValue: "Unnamed outing" });

      const range =
        [formatDateLabel(selectedDownloadOption.startsAt), formatDateLabel(selectedDownloadOption.expiresAt)]
          .filter(Boolean)
          .join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "t/m" })} `);

      const parseParentInfo = (namesStr: string | null | undefined, phonesStr: string | null | undefined) => {
        const splitItems = (value: string | null | undefined) =>
          String(value || "")
            .split(/[,/&;]+/)
            .map((v) => v.trim())
            .filter(Boolean);
        const names = splitItems(namesStr);
        const phones = splitItems(phonesStr);

        return {
          name1: names[0] || "—",
          name2: names[1] || "—",
          phone1: phones[0] || "—",
          phone2: phones[1] || "—",
        };
      };

      const tableRows = list
        .map((row) => {
          const parents = parseParentInfo(asText(row.parentNames), asText(row.parentPhones));
          return `
            <tr>
              <td>${escapeHtml(row.childName || "—")}</td>
              <td>${escapeHtml(formatDateLabel(parseToDate(row.childDateBirth)) || "—")}</td>
              <td>${escapeHtml(row.classroomName || "—")}</td>
              <td>${escapeHtml(parents.name1)}<br/><span class="muted">${escapeHtml(parents.phone1)}</span></td>
              <td>${escapeHtml(parents.name2)}<br/><span class="muted">${escapeHtml(parents.phone2)}</span></td>
              <td>${escapeHtml(row.emergencyContact || "—")}<br/><span class="muted">${escapeHtml(row.emergencyPhone || "")}</span></td>
              <td>${escapeHtml(row.doctorName || "—")}<br/><span class="muted">${escapeHtml(row.doctorPhone || "")}</span></td>
              <td>${formatBool(asBool(row.swimmingDiploma))}</td>
              <td>${formatBool(asBool(row.tripConsent))}</td>
              <td>${escapeHtml(row.allergies || "—")}</td>
              <td>${escapeHtml(row.medicalNotes || "—")}</td>
            </tr>
          `;
        })
        .join("");

      const style = `
        <style>
          * { box-sizing: border-box; }
          @page { size: A4 landscape; margin: 14mm; }
          body { font-family: "Helvetica Neue", Arial, sans-serif; margin: 0; padding: 0; background: #f5f7fb; color: #0f172a; }
          h1 { margin: 0; font-size: 20px; }
          .subtitle { margin: 4px 0 16px; color: #475569; font-size: 14px; }
          .table-wrapper { margin-top: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          thead { background: linear-gradient(90deg, #eef2ff, #e0e7ff); }
          th, td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: left; vertical-align: top; }
          th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #4338ca; }
          td { color: #0f172a; }
          .muted { color: #64748b; font-size: 11px; }
          tbody tr:nth-child(2n) { background: #f8fafc; }
          tbody tr:nth-child(2n+1) { background: #fff; }
        </style>
      `;

      const html = `
        <!doctype html>
        <html lang="nl">
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(title)}</title>
            ${style}
          </head>
          <body>
            <h1>${escapeHtml(title)}</h1>
            <p class="subtitle">${escapeHtml(range || "")}</p>
            <div class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Geb. datum</th>
                    <th>Klas</th>
                    <th>Ouder 1 / tel</th>
                    <th>Ouder 2 / tel</th>
                    <th>Noodcontact</th>
                    <th>Huisarts</th>
                    <th>Zwemdiploma</th>
                    <th>Toestemming</th>
                    <th>Allergieën</th>
                    <th>Medisch</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        document.body.removeChild(iframe);
        setTripActionFeedback({
          type: "error",
          message: t("classrooms.details.trip_export_failed", {
            defaultValue: "Kon geen printvenster openen.",
          }),
        });
        return;
      }

      doc.open();
      doc.write(html);
      doc.close();

      const win = iframe.contentWindow;
      if (win) {
        win.focus();
        win.print();
      }

      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);

      setTripActionFeedback({
        type: "success",
        message: t("classrooms.details.trip_export_ready", {
          defaultValue: "Printvoorbeeld geopend.",
        }),
      });
      setShowDownloadModal(false);
      setSelectedDownloadOption(null);
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setTripActionFeedback({
        type: "error",
        message:
          typeof mapped === "string"
            ? mapped
            : mapped?.message ||
              t("classrooms.details.trip_export_failed", {
                defaultValue: "Download van formulieren is mislukt.",
              }),
      });
    } finally {
      setTripExportLoading(false);
    }
  }, [classroomId, selectedDownloadOption, t, tripExportLoading]);

  const reRequestOutingForChild = useCallback(
    async (childId: string, outing: OutingGroup | null, requestId?: string | null) => {
      if (!childId || !outing || !canManage) return;

      const normalizeDate = (value: string | number | Date | number[] | null | undefined) => {
        if (!value) return null;

        if (Array.isArray(value) && value.length === 3) {
          const [y, m, d] = value;
          const mm = String(m).padStart(2, "0");
          const dd = String(d).padStart(2, "0");
          return `${y}-${mm}-${dd}`;
        }

        if (typeof value === "number") {
          const date = new Date(value > 1e12 ? value : value * 1000);
          return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
        }

        if (typeof value === "string") {
          return value;
        }

        return null;
      };

      try {
        setOutingActionLoading(true);

        if (requestId) {
          const { data } = await http.post(`/outing-info/requests/${requestId}/remind`, {});
          setTripActionFeedback(
            data?.sent === false
              ? {
                  type: "error",
                  message: t("classrooms.details.trip_remind_not_sent", {
                    defaultValue: "The reminder was not e-mailed: no parent of this child can be reached by e-mail through Lumen.",
                  }),
                }
              : {
                  type: "success",
                  message: t("classrooms.details.trip_remind_sent", { defaultValue: "Reminder sent to the parents." }),
                },
          );
          fetchOutingOverview();
          return;
        } else {
          await http.post(`/outing-info/requests/children/${childId}`, {
            name: outing.name || null,
            startsAt: normalizeDate(outing.startsAt),
            expiresAt: normalizeDate(outing.expiresAt),
          });
        }

        setTripActionFeedback({
          type: "success",
          message: t("classrooms.details.trip_request_started", {
            defaultValue: "Ouders ontvangen het toestemmingsformulier in het ouderportaal.",
          }),
        });

        fetchOutingOverview();
      } catch (err) {
        if (requestId && (err as { response?: { status?: number } })?.response?.status === 409) {
          setTripActionFeedback({
            type: "error",
            message: t("classrooms.details.trip_remind_not_open", {
              defaultValue: "This form has already been filled in; no reminder is needed.",
            }),
          });
          fetchOutingOverview();
          return;
        }
        const mapped = mapHttpErrorToMessage(err, t);
        setTripActionFeedback({
          type: "error",
          message:
            typeof mapped === "string"
              ? mapped
              : mapped?.message ||
                t("classrooms.details.trip_request_failed", {
                  defaultValue: "Aanvragen van formulieren is mislukt.",
                }),
        });
      } finally {
        setOutingActionLoading(false);
      }
    },
    [canManage, fetchOutingOverview, t]
  );

  const deleteOutingForChild = useCallback(
    async (childId: string | undefined, outing?: OutingGroup | null) => {
      if (!childId || !canManage) return;

      try {
        setOutingActionLoading(true);

        await http.delete(`/outing-info/children/${childId}`);

        setTripActionFeedback({
          type: "success",
          message: t("classrooms.details.trip_delete_success", {
            defaultValue: "Formuliergegevens verwijderd.",
          }),
        });

        const childKey = String(childId);

        setOutingOverview((prev) =>
          prev.map((item) => {
            if (!outing || item.key !== outing.key) return item;

            const updatedChildren = item.children.filter((c) => String(c.childId) !== childKey);
            const updated = {
              ...item,
              children: updatedChildren,
            };

            return recalcOutingStats(updated);
          })
        );

        setOutingStatus((prev) => prev.filter((row) => String(row.childId) !== childKey));

        await fetchOutingOverview();
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setTripActionFeedback({
          type: "error",
          message:
            typeof mapped === "string"
              ? mapped
              : mapped?.message ||
                t("classrooms.details.trip_delete_failed", {
                  defaultValue: "Delete failed.",
                }),
        });
      } finally {
        setOutingActionLoading(false);
      }
    },
    [canManage, fetchOutingOverview, t]
  );

  const deleteOutingById = useCallback(
    async (outingId: string | undefined) => {
      if (!outingId || !canManage) return;

      try {
        setOutingActionLoading(true);

        await http.delete(`/outing-info/outings/${outingId}`);

        setTripActionFeedback({
          type: "success",
          message: t("classrooms.details.outing_delete_success", {
            defaultValue: "Outing deleted.",
          }),
        });

        fetchOutingOverview();
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setTripActionFeedback({
          type: "error",
          message:
            typeof mapped === "string"
              ? mapped
              : mapped?.message ||
                t("classrooms.details.outing_delete_failed", {
                  defaultValue: "Deleting the outing failed.",
                }),
        });
      } finally {
        setOutingActionLoading(false);
      }
    },
    [canManage, fetchOutingOverview, t]
  );

  useEffect(() => {
    if (canRead) {
      fetchOutingOverview();
    }
  }, [canRead, fetchOutingOverview]);

  useEffect(() => {
    if (!showOutingMenu && !showStatusPopover) return undefined;

    const handleClick = (e: MouseEvent) => {
      if (
        showOutingMenu &&
        outingMenuRef.current &&
        !outingMenuRef.current.contains(e.target as Node) &&
        outingMenuButtonRef.current &&
        !outingMenuButtonRef.current.contains(e.target as Node)
      ) {
        setShowOutingMenu(false);
      }

      if (showStatusPopover && statusPopoverRef.current && !statusPopoverRef.current.contains(e.target as Node)) {
        setShowStatusPopover(false);
        setStatusPopoverOuting(null);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showOutingMenu, showStatusPopover]);

  return {
    tripActionFeedback,
    setTripActionFeedback,
    tripRequestLoading,
    tripExportLoading,
    outingActionLoading,

    showOutingModal,
    setShowOutingModal,
    outingStartsAt,
    setOutingStartsAt,
    outingExpiresAt,
    setOutingExpiresAt,
    outingName,
    setOutingName,
    outingAddToClassroomCalendar,
    setOutingAddToClassroomCalendar,
    outingModalError,
    setOutingModalError,
    showOutingOverviewModal,
    setShowOutingOverviewModal,

    showDownloadModal,
    setShowDownloadModal,
    downloadOptions,
    downloadOptionsLoading,
    selectedDownloadOption,
    setSelectedDownloadOption,

    outingStatus,
    outingStatusLoading,

    outingOverview,
    outingOverviewLoading,
    outingStatusExpanded,
    setOutingStatusExpanded,
    activeOutingKey,
    setActiveOutingKey,

    showStatusPopover,
    setShowStatusPopover,
    statusPopoverOuting,
    setStatusPopoverOuting,
    showOutingMenu,
    setShowOutingMenu,

    outingMenuRef,
    outingMenuButtonRef,
    statusPopoverRef,

    sortedOutings,
    firstOuting,

    fetchOutingOverview,
    fetchOutingStatus,
    loadDownloadOptions,
    submitOutingRequest,
    handleDownloadTripForms,
    reRequestOutingForChild,
    deleteOutingForChild,
    deleteOutingById,
  };
};

export type UseOutingsReturn = ReturnType<typeof useOutings>;
